/**
 * The numbers on the landing page, and where each one comes from.
 *
 * Every figure here is measured, not estimated. This file exists so the provenance
 * travels with the value: a developer audience will check these, and a number nobody
 * can trace is worse than no number. If a benchmark is re-run and a figure moves, it
 * moves here and nowhere else.
 *
 * Sources are paths in the archis-cli workspace, so a claim can be re-derived.
 *
 * ONE BUILD, STATED
 * -----------------
 * Everything below was measured with **Archis 0.13.0** — the shipped release — over a
 * 22-repository corpus, re-run from scratch on that build rather than inherited from
 * an earlier one. This matters more than it sounds, and the file has been burned by
 * it before: figures once carried here came from a 0.2.0-era run, and re-measuring the
 * same repositories on 0.5.0 moved representation reduction DOWN by 4 to 36 points,
 * because capture added to lift symbol recall also enlarges the compact IR. A
 * reduction figure without a build attached is unfalsifiable.
 *
 * The 0.13.0 re-run reproduces the 0.6.0 corpus almost exactly — pooled −72.8% again,
 * per-repo reduction and coverage unchanged to the decimal on every row. That is the
 * expected result and it is worth stating plainly: 0.7.0 through 0.13.0 added a
 * runtime, an artifact store, parsers, filters and a document index, and changed
 * nothing about how source code is projected. What DID move is reported as such —
 * the recall gate (16/20 → 18/20), incremental convergence (17/21 → 21/21), and a
 * crash found by this very campaign. The corpus is `bench/official/manifest.json`
 * cohorts 1 and 4, all pinned to released tags.
 *
 * THREE BENCHMARKS, NEVER MIXED
 * -----------------------------
 * The page used to lead with "30% fewer tokens", which measured one file's compact
 * projection against its source across ten files. That is a real number about
 * *representation*, and it is the wrong number to sell, because it is not what a task
 * costs. These are separate measurements with separate methods, so they get separate
 * sections and separate headings:
 *
 *   TASK_CONTEXT       what an agent spends to do a job through Archis vs on full source.
 *                      20 paired tasks per repo, deterministic tool sequences over the
 *                      real MCP surface, every exposure priced with tiktoken.
 *   REPRESENTATION     how much smaller the compact projection of a repository is than
 *                      its source. Whole public repos, tokenized the same way.
 *   RUNTIME            what everything *else* an agent turn produces costs — test runs,
 *                      build logs, linter output — and whether the part that was
 *                      removed can still be read back. Fixtures with oracles, not
 *                      repositories.
 *
 * Presenting them as one proof would be the sort of thing a buyer catches, and the
 * whole point of putting real numbers on a page is that they hold up.
 */

import {
  BENCHMARK_RUN,
  BENCHMARK_SOURCES,
  COHORT5_LANGUAGE_RUN,
  COHORT5_RUN,
  FASE_B_RUN,
} from './benchmarks.generated'

/** Injected by vite.config.ts: the newest file in `entities/releases/notes`. */
declare const __CLI_VERSION__: string

export interface Metric {
  /** The number, as it should read. */
  value: string
  /** Unit or suffix, set small beside the value. */
  unit?: string
  /** One line saying what it means to the reader. Not what it measures — what it buys. */
  meaning: string
  /** Where it came from, shown on hover and in the footnote. */
  source: string
}

/**
 * Public marketing hierarchy. Indexing, task-context, and support are never
 * pooled into one campaign number (the old "116 repositories" composite was
 * 22 + 94, two benches). The shipping CLI version is for chrome only — it is
 * not a claim that every figure below was re-run on that build.
 *
 * It is the newest published release, injected at build time from the release-note
 * file names, so it moves with every release instead of waiting for someone to edit
 * it here.
 */
export const CLI_VERSION = __CLI_VERSION__

/**
 * The cohort-5 figures, computed from one pinned public run (#1316).
 *
 * They used to be typed in here, and they drifted from the measurement they quoted
 * (#1207). The headline indexing gate also came from an internal run from before
 * 0.14.0 that was never published (94 corpora, 538,745 files), which no reader could
 * check. Every cohort-5 number on the site is now read from
 * `benchmarks.generated.ts`, which `scripts/benchmarks.mjs` computes from the files
 * of `terragov/archis-benchmarks@run/<version>` vendored under src/data/benchmarks/.
 * CI fails when the two disagree, or when the vendored files are not the tag's.
 * Moving to a newer run is `node scripts/benchmarks.mjs sync <version>`.
 *
 * What the public run does not record is not stated. It counts no parse failures
 * (`parse_failed` is null on every row), so the site no longer claims "0 parse
 * failures". Determinism is what the run measures instead, per repository.
 */
const RUN = COHORT5_RUN
export const COHORT5_BUILDS = {
  build: `chi ${BENCHMARK_RUN.version}`,
  tag: BENCHMARK_RUN.tag,
  repos: RUN.repos,
  unmeasured: RUN.unmeasured.join(', '),
} as const

const fmt = (n: number) => n.toLocaleString('en-US')
const PUBLIC_RUN = `${BENCHMARK_RUN.repository}@${BENCHMARK_RUN.tag} (commit ${BENCHMARK_RUN.commit.slice(0, 12)})`

/** The band's three counts as the pages quote them in prose. */
export const INDEXING_GATE = {
  repos: String(RUN.repos),
  files: fmt(RUN.files),
  deterministic: `${RUN.hashStable}/${RUN.repos}`,
  /** The same fact in words, for prose that should not carry two more figures. */
  deterministicInWords:
    RUN.hashStable === RUN.repos ? 'every one deterministic' : `${RUN.hashStable}/${RUN.repos} deterministic`,
} as const

/** Lead band: cohort-5 indexing, from the pinned public run. */
export const INDEXING: Metric[] = [
  {
    value: INDEXING_GATE.repos,
    meaning: 'pinned public repositories, indexed',
    source: `${PUBLIC_RUN} — ${BENCHMARK_SOURCES.repos}. Not pooled with the 22-repo task-context table.`,
  },
  {
    value: INDEXING_GATE.files,
    meaning: 'files indexed across them',
    source: `${PUBLIC_RUN} — ${BENCHMARK_SOURCES.files}.`,
  },
  {
    value: INDEXING_GATE.deterministic,
    meaning: 'produce the same graph hash on every repetition',
    source: `${PUBLIC_RUN} — ${BENCHMARK_SOURCES.hashStable}.`,
  },
  {
    value: `~${Math.round(RUN.medianReduction)}`,
    unit: '%',
    meaning: `smaller median representation than source, over the same ${RUN.repos}`,
    source: `${PUBLIC_RUN} — median reduction ${RUN.medianReduction}%, ${BENCHMARK_SOURCES.medianReduction}.`,
  },
]

/**
 * The public file a reader can open to check the band above, row by row. The same
 * run, so its median is the band's median.
 */
export const PUBLIC_COHORT5 = {
  version: BENCHMARK_RUN.version,
  path: `runs/${BENCHMARK_RUN.version}/fase_a/cohort5.json`,
  repos: RUN.repos,
  medianReduction: RUN.medianReduction,
} as const

/** Second band: paired agent tasks. Never pooled with INDEXING. */
export const TASK_CONTEXT_BAND: Metric = {
  value: '89.4–95',
  unit: '%',
  meaning: 'less task context on 20 repos vs a full-source agent — a separate policy band, not the indexing median',
  source:
    'TASK_CONTEXT in this file: 20 pinned public repositories, full escalating policy vs a full-source agent doing the same task. Range is detekt 89.4% through PowerShell 95.0%. Not a pooled mean with cohort 5.',
}

/** Support copy. Not headlines. */
export const SUPPORTING_PUBLIC: Metric[] = [
  {
    value: '29',
    meaning: 'languages the compiler lowers',
    source:
      'archis-cli language packs / terragov/archis-benchmarks README language table. Count of supported languages, not a bench score.',
  },
  {
    value: String(FASE_B_RUN.strict),
    unit: '%',
    meaning: `strict symbol recall on Fase B at ${BENCHMARK_RUN.version} — ${FASE_B_RUN.repos} repos, ${FASE_B_RUN.languages} languages`,
    source: `${BENCHMARK_RUN.repository} ${BENCHMARK_SOURCES.faseB}, ${COHORT5_BUILDS.build}: mean strict recall (same name, same file) over ${FASE_B_RUN.repos} repos / ${FASE_B_RUN.languages} languages; the lenient figure is ${FASE_B_RUN.lenient}%. Optional support figure; not a headline.`,
  },
]

/** The privacy contract, in one line. */
export const PRIVACY_CONTRACT: Metric = {
  value: '0',
  meaning: 'source endpoints — there is no API that accepts source',
  source: 'There is no endpoint that accepts source. See the privacy page.',
}

/**
 * The four figures in the hero.
 *
 * Every one of them is stated as what the reader gets, not as what we measured. The
 * measurement is still the source string underneath, unchanged — the wording moved,
 * the provenance did not.
 *
 * Edit-target recall and the negatives gate are deliberately NOT here. They are the
 * numbers that decide whether you *believe* the reduction, which makes them evidence,
 * not headline: they sit in the supporting band inside the benchmark section, next to
 * the tables they qualify.
 */
export const HEADLINE: Metric[] = [
  {
    value: '22–43',
    unit: '%',
    meaning: 'less money per task, with a real agent doing a real task',
    source:
      'Archis 0.18.0, OpenAI Codex CLI with gpt-5.5, three cells of bench/infinite on prometheus pinned at e6cfa720fbe6, priced from provider run totals at 3 / 0.30 / 15 dollars per million for input, cached input and output. Level 8 at effort high 0.78x over 8 valid pairs across three independent campaigns; level 12 0.75x over 8 pairs; level 16 0.57x over 4 pairs with complete separation. The range is the three cells, not a best case: the widest and the narrowest are both in it. Money is the noisiest axis here because cache hits move billed input more than work does — the input figure beside this one is the stable measure',
  },
  {
    value: '35–62',
    unit: '%',
    meaning: 'less context read, which is 74–86% of what an agent turn costs',
    source:
      'Same three cells and the same campaigns. Total input tokens through Archis against the same task on the agent’s own tools: level 8 at effort high 0.54x (8 pairs), level 12 0.65x (8 pairs), level 16 0.38x with complete separation (4 pairs). Input is where the bill is: on a level-8 run it is 74–86% of the total',
  },
  {
    value: '5',
    unit: '%',
    meaning: 'run-to-run variance in what the model writes, against 82% native',
    source:
      'The one measure that separates completely in every campaign and every pool: visible output tokens, 0.58x, over 8 pairs and three independent campaigns at level 8 effort high. Through Archis 3,095–3,251 tokens — a 5% spread. On its own tools 4,105–7,492 — 82%. Archis asks the index three times and writes an answer; the native arm writes a Go AST parser, and writes a different one every run. A predictable bill is a property of the first shape and not of the second',
  },
  {
    value: '0',
    meaning: 'lines of your code uploaded, ever',
    source: 'There is no endpoint that accepts source. See the privacy page.',
  },
]

/**
 * The shipping build, on the cell with the most repetitions.
 *
 * Eight valid pairs across three independent campaigns, same corpus, same pinned
 * commit, same prompt, same question set, same reasoning effort. Nothing about
 * Archis reaches the prompt: the Archis arm is created by handing the agent an MCP
 * registration and the control arm by handing it none.
 *
 * WHY THIS TABLE SAYS "mean" WHERE THE OLD ONE SAID "every run"
 * -------------------------------------------------------------
 * Because that is what eight pairs support and three did not. The first campaign
 * on this cell read 0.62x on money and separated completely; the second read
 * 0.85x, the third 0.87x. Pooled it is 0.78x with the ranges overlapping. The
 * 0.62x was not wrong, it was two pairs — and publishing it would have been the
 * same mistake this file exists to prevent.
 *
 * The reason nothing but `visible` separates is not Archis. Across its own runs at
 * fixed everything, the native arm varies 2.2x in money and 5.7x in input; Archis
 * varies 1.9x and 2.5x, and on visible output 1.05x. Three repetitions cannot
 * separate against that dispersion, and more repetitions do not fix a baseline
 * that is irreproducible with itself — they only make the mean honest.
 */
export const AGENT_LOOP = {
  pairs: 8,
  rows: [
    {
      measure: 'Output the model wrote',
      control: '5,477',
      chi: '3,171',
      delta: '42% less',
      separated: true,
      win: true,
      note: 'Output minus provider-reported reasoning: tool arguments and the answer. Control 4,105–7,492, Archis 3,095–3,251. The only measure that separates completely, and it does so in all three campaigns and in the pool. Control spends it writing a Go AST parser; Archis spends it on three tool calls.',
    },
    {
      measure: 'Input tokens',
      control: '190,474',
      chi: '102,089',
      delta: '46% less',
      separated: false,
      win: true,
      note: 'Control 94,490–536,467, Archis 62,220–155,259. Ranges overlap because the control arm spans 5.7x across its own runs. Input is 74–86% of a level-8 bill, so this is the figure that moves money.',
    },
    {
      measure: 'Cost per run',
      control: '$0.289',
      chi: '$0.226',
      delta: '22% less',
      separated: false,
      win: true,
      note: 'Control $0.2231–$0.4899, Archis $0.1701–$0.3239. Priced at 3 / 0.30 / 15 dollars per million for input, cached input and output. Cache hits move this more than work does, which is why the input row above is the stable one.',
    },
    {
      measure: 'Wall clock',
      control: '224 s',
      chi: '157 s',
      delta: '30% faster',
      separated: false,
      win: true,
      note: 'Control 155–375 s, Archis 133–189 s. Three MCP calls against eight to fifteen shell commands, one of which compiles Go.',
    },
    {
      measure: 'Reasoning tokens',
      control: '3,927',
      chi: '4,239',
      delta: 'level',
      separated: false,
      win: false,
      note: 'Control 2,651–6,854, Archis 1,864–5,219. Heavily overlapped and slightly against us on the mean. Reported as level rather than as a win.',
    },
    {
      measure: 'Answer accuracy',
      control: '97.4%',
      chi: '97.5%',
      delta: 'level',
      separated: false,
      win: false,
      note: 'Composite score over eight runs an arm, graded by the same oracle. A difference of one thousandth is parity, and it is reported as parity — the cost above is not bought with quality.',
    },
  ],
  source:
    'archis-cli/results/infinite/20260812T114321Z-…-real-high, 20260812T132647Z-…-real-high-2 and 20260812T172605Z-…-sep-high — bench/infinite level 8 on prometheus pinned at e6cfa720fbe6, OpenAI Codex CLI 0.138.0 with gpt-5.5 at reasoning effort high, three campaigns of three repetitions an arm. One repetition was lost to a provider refusal ("Selected model is at capacity", zero output tokens); it is dropped from *both* arms rather than from Archis alone, because keeping control’s half would compare eight control runs against seven Archis runs on a different sample. Unpaired, that one run alone reads 0.42x on money.',
  scope:
    'One repository, one task shape, one model. Every figure is a mean over eight pairs with its range beside it, and only "Output the model wrote" separates completely — every Archis run beating every native run, which for two sets of eight has probability 1/12,870 under exchangeability. The others are means with overlapping ranges and are labelled as such.',
} as const

/**
 * What the literal scan is worth, isolated.
 *
 * The cleanest result on this page, because only one thing differs between the
 * two columns: whether the `contains` tool exists in the build. Same corpus, same
 * pinned commit, same prompt, same questions, same effort, same model.
 *
 * The questions it answers are the ones a projection cannot: the needle is a
 * five-word phrase out of a comment, which has no declaration, casts no edge and
 * cannot be guessed from a name. Without a way to ask, the agent bought all
 * twelve scoped files and spent 2.2x the control arm's input. With one, it asks
 * once and stops.
 */
export const CONTAINS_AB = {
  rows: [
    { measure: 'Cost per run', without: '1.36×', with_: '0.78×', note: 'Against the same task on the agent’s own tools. Without the scan the Archis arm is 36% more expensive than native; with it, 22% cheaper.' },
    { measure: 'Input tokens', without: '2.24×', with_: '0.54×', note: 'The largest single move on this page. 390,460 tokens against 102,089.' },
    { measure: 'MCP calls', without: '7.3', with_: '3.0', note: 'Without the scan: get_files(view="full") on all twelve, then search, get_source, compact_select and get_symbols — every retrieval tool except one that could answer.' },
    { measure: 'Files bought in full', without: '3 of 3 runs', with_: '0 of 8 runs', note: 'The agent stated the reason itself before buying them: "only to verify literal-token containment across file bodies, since outlines do not include every token in comments or statements."' },
    { measure: 'Text-only questions', without: '8 of 9', with_: '24 of 24', note: 'Both arms. The control arm answers them by reading the files off disk; Archis answers them from the index for about two hundred tokens.' },
  ],
  source:
    'archis-cli/results/infinite/20260812T031639Z-…-text-only-real (without) against the three campaigns above (with). Identical in every respect but the build. The scan itself is verified separately against the oracle by bench/infinite/contains_probe.py: three answers exact, 24 line numbers confirmed against the files on disk, every occurrence count exact, for 198 tokens against the 40,484 a full read costs — and at level 12, 171 against 77,519.',
} as const

/**
 * The other cells on the shipping build, and the two places it is not a win.
 *
 * Published in the same table and the same type as the wins. A reader who finds
 * a loss themselves stops believing the wins; a reader handed it believes both.
 */
export const CELLS = [
  {
    cell: 'Level 12 — thirty-two files',
    pairs: '8 pairs',
    money: '25% less',
    input: '35% less',
    quality: 'Archis ahead',
    note: 'Money 0.75×, input 0.65×, visible output 0.72×, wall clock 0.65×. Nothing separates: the control arm spans $0.129–$0.500 across its own runs, 3.9×, and its cheapest run is cheaper than Archis’s cheapest. Quality 0.9770 against 0.9588 — the gap is control, not Archis: of its eight runs one scored 0.8539 and two produced no gradable answer at all, where all eight Archis runs landed between 0.972 and 0.981.',
  },
  {
    cell: 'Level 16 — eighty files',
    pairs: '4 pairs',
    money: '43% less',
    input: '62% less',
    quality: 'Archis behind',
    note: 'Five of six measures separate completely — money $0.7017–$0.8156 against $0.3594–$0.5137, input 517k–782k against 198k–366k, plus output, reasoning and wall clock. This cell was 1.37× against us before the literal scan. The quality column is why it is not the headline: at eighty files both arms are bimodal, reporting all eighty file rows or none, and Archis does the latter twice in four runs against control’s once. Mean 0.6145 against 0.7979. It is not an output ceiling — a complete answer is ~15,000 tokens against a 32,000 cap, and the failing runs emit 6,000. The answer fits and does not get written, in both arms, and no measurement we have explains which runs do it.',
  },
  {
    cell: 'Byte-fidelity suite',
    pairs: '2 pairs',
    money: 'level',
    input: 'level',
    quality: 'both 0.9999',
    note: 'The suite that demands verbatim source, which compression cannot help with, and it finally has a comparison after four campaigns of two failures. Both arms reproduce the files at 0.9999 byte accuracy for the same money, 1.05×. Parity is the correct outcome here and this is the first time it has been shown rather than asserted. At level 8 the suite still produces nothing: it asks for ~47,000 tokens of verbatim source in the answer and neither arm writes that, so the harness now warns before the run instead of after.',
  },
] as const

/** The supporting band, inside the benchmark section. Four numbers, no table. */
export const SUPPORTING: Metric[] = [
  {
    value: '−72.8',
    unit: '%',
    meaning: 'smaller representation, pooled over 22 public repos',
    source:
      'results/official/REPORT_cohort1_4_multilang.md — 52,119,711 → 14,171,466 tokens across 22 repositories in 8 languages, Archis 0.13.0 (compact.v1). At 0.18.0 a projection that would come out larger than its source is no longer emitted at all — the stored block, borrowed from DEFLATE — so a per-file reduction can no longer be negative and neither can a pooled one',
  },
  {
    value: '18/20',
    meaning: 'repos where the symbol you need fits the budget (recall ≥0.90)',
    source:
      'results/context_policy/ — edit_target_recall at a 1,600-token budget, seeded only by the task query, Archis 0.13.0. Up from 16/20: Avalonia and guava both moved 0.85 → 0.95. Two corpora still miss the gate — RxJava 0.65 and nvm 0.65 — where one name covers hundreds of declarations (RxJava declares run 1,779 times) and a bare query cannot say which one you meant',
  },
  {
    value: '10.5k',
    meaning: 'files indexed in 18 s — angular, 116k symbols',
    source:
      'results/official/angular/fase_a.json — cold index of 10,513 files to 116,312 symbols in 18.0 s at 145 MB peak RSS, Archis 0.13.0 (wall time varies a few seconds between runs; this is the recorded measurement, not a best-of)',
  },
  {
    // This slot held "21× fewer invented facts", from the Claude campaign. It went
    // when that campaign did, and it should not come back on the strength of that
    // measurement alone: re-measured on Codex, the two arms invent the same amount —
    // 50.0 a run through Archis against 50.7 on native tools at effort low, 50.3 against
    // 51.3 at high. Whatever produced 49-against-2.3 on one agent does not reproduce
    // on the other, so the claim is not one this page can make.
    //
    // What replaced it is narrower and checkable: not "the model invents less" but
    // "the index does not", verified against the language's own parser.
    value: '1,877',
    meaning: 'imports read exactly right across 200 Go files — none missed, none invented',
    source:
      'Audited against `go/parser` itself, not against Archis: 200 non-test Go files of prometheus at e6cfa720fbe6, every import compared to what the Go standard library reports for the same file. Precision 1.0000, recall 1.0000, Archis 0.17.0. The previous release served nine struct fields as imports across four of twelve files, which is the defect this audit exists to catch',
  },
  {
    // New at 0.18.0. It replaces nothing; it is the first figure on this page for
    // the tool that turned the level-8 high cell from a loss into a win.
    value: '204×',
    meaning: 'cheaper to ask whether a string is in a file than to read the file',
    source:
      'bench/infinite/contains_probe.py against the level-8 oracle on prometheus at e6cfa720fbe6, Archis 0.18.0: `contains` answers all three literal-containment questions exactly — match sets equal to the oracle, 24 line numbers confirmed against the files on disk, every occurrence count exact — for 198 tokens against the 40,484 that reading the twelve files costs. At level 12, 171 against 77,519, which is 453×. It returns paths, counts and line numbers and never file contents',
  },
  {
    value: '0/5',
    meaning: 'made-up names answered — it never invents a match',
    source:
      'results/official/REPORT_fase_b_precision.md — negatives gate, every repo (earlier build; a structural property of the resolver rather than a tuned number)',
  },
  {
    value: `${RUN.incrementalEqual}/${RUN.repos - RUN.notProbed}`,
    meaning: 'probed cohort-5 repositories where an incremental re-index equals a clean rebuild',
    source: `${PUBLIC_RUN} — ${BENCHMARK_SOURCES.incremental}. ${RUN.notProbed} of the ${RUN.repos} were not probed and are not counted either way.`,
  },
]

/**
 * Cohort 5 — the fifteen languages and formats added after cohort 4, per language.
 *
 * Kept apart from everything above because it is a different campaign on a
 * different corpus, and pooling the two would be exactly the kind of blend this
 * file exists to prevent.
 *
 * Computed from the pinned public run (#1316): `repos`, `files` and `symbols` from
 * fase_a/cohort5_languages.json, where `repos` is how many cohort-5 repositories
 * hold a file of that language. `recall` is the mean symbol recall against
 * universal-ctags over that language's Fase B repositories (fase_b/recall.json),
 * which is a separate population of five or so repositories per language, chosen
 * for the oracle. The rows this table used to carry by hand were the 0.13.0-era
 * sweep, and several were stale for reasons the language closures documented
 * (archis#514, #533, #542, #553).
 *
 * `recall: null` means NO ORACLE in the run — Fase B has no ctags comparison for
 * the language (Dart, PowerShell, SQL on 0.49.0). That is a different statement
 * from recall 0 and must never render as a zero bar.
 */
export const COHORT5 = COHORT5_LANGUAGE_RUN

/**
 * Fase C on the cohort-5 languages: does an agent actually fix the bug?
 *
 * Three baselines on the same mutation: **A** full source and the agent explores
 * freely, **C** Archis compact plus source on demand, **D** the graph-only repair
 * agent. D is published even though it loses, because the interesting result is
 * not the score — it is *how* it loses. Where D fails, the dominant outcome is
 * LOCALIZED_NO_PATCH: retrieval puts the agent on the right file and the agent
 * declines to write the fix. That points at the repair prompt, not at the graph,
 * and pretending otherwise by omitting the row would be the easy lie.
 */
export const FASE_C_COHORT5 = {
  languages: 8,
  a: { fixed: 31, of: 32, medianTokens: 163713 },
  c: { fixed: 30, of: 32, medianTokens: 96820 },
  d: { fixed: 10, of: 32, medianTokens: 8762 },
  note:
    'One TIMEOUT (ruby, baseline D) never returned an answer at all and is counted as a miss, which understates D by one cell — a run the agent never answered is not a run it failed. There is no retry: each mutation × repetition × baseline runs exactly once.',
  source: 'results/official/REPORT_cohort5.md — Fase C, Archis 0.13.0',
}

/**
 * Fase C per language: the eight rows behind the 1.7x headline.
 *
 * `a`/`c`/`d` are the three baselines on the same mutation, out of 4 runs each
 * (2 mutations x 2 repetitions). `ratioAD` is the median-token ratio between the
 * full-source agent and the graph-only repair agent, which is the figure that says
 * what the graph costs when it works.
 *
 * swift is the row worth reading twice: C fixed 2 of 4 where A fixed 4 of 4, the
 * only language where source-on-demand lost ground. It stays because a table that
 * only contains 4/4 rows tells the reader nothing about when to distrust it.
 */
export const FASE_C_LANGS = [
  { lang: 'Dart', repo: 'drift', a: 4, c: 4, d: 4, tokensA: 158024, tokensD: 8191, ratioAD: 19 },
  { lang: 'Elixir', repo: 'nimble_csv', a: 4, c: 4, d: 0, tokensA: 169402, tokensD: 12384, ratioAD: 14 },
  { lang: 'Lua', repo: 'inspect.lua', a: 4, c: 4, d: 0, tokensA: 129037, tokensD: 6395, ratioAD: 20 },
  { lang: 'PowerShell', repo: 'pester', a: 4, c: 4, d: 1, tokensA: 413956, tokensD: 7351, ratioAD: 56 },
  { lang: 'Ruby', repo: 'dry-monads', a: 4, c: 4, d: 1, tokensA: 117600, tokensD: 10798, ratioAD: 11 },
  { lang: 'Scala', repo: 'cats-parse', a: 3, c: 4, d: 0, tokensA: 300541, tokensD: 9333, ratioAD: 32 },
  { lang: 'Swift', repo: 'swift-argument-parser', a: 4, c: 2, d: 0, tokensA: 214033, tokensD: 10966, ratioAD: 20 },
  { lang: 'Zig', repo: 'zig-clap', a: 4, c: 4, d: 4, tokensA: 89794, tokensD: 5743, ratioAD: 16 },
] as const

/**
 * The cohort-5 gates, as prose the page can render rather than a pass/fail glyph.
 *
 * Each one names what was NOT measured as precisely as what was. `azure-powershell`
 * is the reason: its idempotency probe did not fail, it never finished — the second
 * index of a 22 GiB graph ran past the harness's one-hour ceiling. Rendering that as
 * a red cell would be a lie in the flattering direction for every other tool that
 * simply does not run at that size.
 *
 * Every row is computed from the pinned public run (benchmarks.generated.ts). The
 * run records no parse failures, so there is no parse-failure row: a gate the data
 * does not measure is not a gate the page states.
 */
export const COHORT5_GATES = [
  {
    gate: 'Determinism',
    result: `${RUN.hashStable}/${RUN.repos} produce an identical canonical hash across repetitions, on ${COHORT5_BUILDS.build}. ${COHORT5_BUILDS.unmeasured}, whose graph the run did not keep, is not counted.`,
  },
  {
    gate: 'Incremental == clean rebuild',
    // Each count of not-probed repositories is the run's own reason, matched exactly
    // (SKIP_REASONS in scripts/benchmark-figures.mjs), and a reason with no sentence
    // here fails the generation instead of being counted under one of these.
    result: `${RUN.incrementalEqual}/${RUN.repos} equal, ${RUN.incrementalDiffers.length} different (${RUN.incrementalDiffers.join(', ')}, under investigation), ${RUN.notProbed} not probed. Of the ${RUN.notProbed}, ${RUN.notProbedXml} are XML, which cannot take an appended element without its schema, and ${RUN.notProbedNoSourceFile} have no file of their language the probe can use outside hidden, ignored, test and build directories. ${COHORT5_BUILDS.unmeasured}, whose graph the run did not keep, is not counted.`,
  },
  {
    gate: 'Compression',
    result: `Median reduction ${RUN.medianReduction}%, over the ${RUN.repos}.`,
  },
] as const

/**
 * Every cohort-5 repository, with the coverage that qualifies its reduction.
 *
 * Ordered by reduction, best to worst, for the same reason the cohort-1/4 table is:
 * read down the reduction column and coverage moves against it, and on 94 rows the
 * relationship is no longer arguable. The top of this table is where it is most
 * visible -- nvim-lspconfig drops 97.7% of its tokens and keeps 8% of its
 * declarations, hermitage drops 98.5% because it indexed zero SQL files (its SQL
 * lives inside Markdown) and keeps 15%.
 *
 * THE BOTTOM TWO ROWS ARE NEGATIVE, and 0.18.0 closes the mechanism behind them.
 * The compact projection of openshift-origin came out 2.4x LARGER than its source
 * (109.5M to 264.6M tokens) at 91% coverage, and docker-getting-started 2.7x
 * larger at 65%. Their per-file mean reductions were healthy (69% and 49%), which
 * said the pooled figure was being dominated by a few files whose projection
 * explodes rather than by a uniform regression.
 *
 * It was, and it is now measured on the real corpus rather than inferred.
 * docker-getting-started at its pinned 94d4031 reproduces -166.9% exactly with the
 * guard disabled — which is what makes it a measurement of this row and not of
 * something adjacent — and reads +30.2% with it. The cause is ONE FILE:
 * `app/src/static/js/babel.min.js`, 259,455 source tokens projecting to 1,141,766,
 * 4.4x, because minified JavaScript on a single line has an AST that explodes. The
 * other four fallbacks are 20-50 tokens of noise. 882,339 tokens avoided across 5
 * files of 37.
 *
 * From 0.18.0 a projection that comes out no smaller than its source is not
 * emitted at all — the stored block, borrowed from DEFLATE — so a per-file
 * reduction cannot be negative and neither can a pooled one. The rows below keep
 * their measured 0.13.0 values because that is what those builds did.
 *
 * openshift-origin is NOT re-measured. Its published figure covers the whole
 * repository at 109.5M source tokens; the 2,392-file YAML subset reads +79.6%
 * before the guard, which does not reproduce it, and a number that does not
 * reproduce the one it replaces is not evidence.
 *
 * `inc` is the incremental-vs-rebuild probe: 'equal', 'differs', or null for the 21
 * corpora with no probe-able source file. null is NOT a failure and must never
 * render as one.
 *
 * Generated from results/official/_fase_a_summary_cohort5.json (reduction, incremental),
 * per-repo fase_a.json (coverage) and index_bench_cohort5.json (files, symbols).
 */
export const COHORT5_REPOS = [
  { repo: 'hermitage', lang: 'SQL', files: 0, symbols: 11, reduction: 98.5, coverage: 15, inc: null },
  { repo: 'nvim-lspconfig', lang: 'Lua', files: 851, symbols: 565, reduction: 97.7, coverage: 8, inc: 'equal' },
  { repo: 'powershellget', lang: 'PowerShell', files: 34, symbols: 118, reduction: 97.7, coverage: 20, inc: 'equal' },
  { repo: 'dbatools', lang: 'PowerShell', files: 1777, symbols: 7696, reduction: 96.7, coverage: 54, inc: 'equal' },
  { repo: 'lua-classic', lang: 'Lua', files: 1, symbols: 18, reduction: 96.6, coverage: 21, inc: 'equal' },
  { repo: 'prometheus-helm-charts', lang: 'YAML', files: 890, symbols: 10051, reduction: 96.1, coverage: 43, inc: 'equal' },
  { repo: 'graphql-over-http', lang: 'GraphQL', files: 3, symbols: 387, reduction: 95.1, coverage: 37, inc: null },
  { repo: 'inspect.lua', lang: 'Lua', files: 4, symbols: 33, reduction: 94.9, coverage: 28, inc: 'equal' },
  { repo: 'pester', lang: 'PowerShell', files: 326, symbols: 1985, reduction: 94.7, coverage: 66, inc: 'equal' },
  { repo: 'gradle-build-scan-quickstart', lang: 'Groovy', files: 5, symbols: 13, reduction: 94.5, coverage: 41, inc: null },
  { repo: 'bitnami-charts', lang: 'YAML', files: 3378, symbols: 85655, reduction: 94.5, coverage: 11, inc: 'equal' },
  { repo: 'helm-examples', lang: 'YAML', files: 6, symbols: 34, reduction: 93.7, coverage: 43, inc: 'equal' },
  { repo: 'kong', lang: 'Lua', files: 1884, symbols: 16132, reduction: 91.5, coverage: 67, inc: 'equal' },
  { repo: 'runner-images', lang: 'PowerShell', files: 197, symbols: 2148, reduction: 91.1, coverage: 31, inc: 'equal' },
  { repo: 'dart-web', lang: 'Dart', files: 433, symbols: 8459, reduction: 90.9, coverage: 75, inc: 'equal' },
  { repo: 'kotlin-dsl-samples', lang: 'Groovy', files: 216, symbols: 566, reduction: 90.8, coverage: 54, inc: null },
  { repo: 'plenary.nvim', lang: 'Lua', files: 114, symbols: 441, reduction: 90.6, coverage: 45, inc: 'equal' },
  { repo: 'known-folders', lang: 'Zig', files: 3, symbols: 89, reduction: 89.9, coverage: 34, inc: 'equal' },
  { repo: 'brew', lang: 'Ruby', files: 1955, symbols: 11988, reduction: 88.2, coverage: 46, inc: 'equal' },
  { repo: 'nimble_csv', lang: 'Elixir', files: 6, symbols: 88, reduction: 88.0, coverage: 30, inc: 'equal' },
  { repo: 'dry-monads', lang: 'Ruby', files: 75, symbols: 708, reduction: 86.8, coverage: 46, inc: 'equal' },
  { repo: 'graphql-spec', lang: 'GraphQL', files: 4, symbols: 324, reduction: 86.6, coverage: 37, inc: null },
  { repo: 'zig-clap', lang: 'Zig', files: 13, symbols: 200, reduction: 86.5, coverage: 60, inc: 'equal' },
  { repo: 'secure_headers', lang: 'Ruby', files: 48, symbols: 448, reduction: 86.2, coverage: 53, inc: 'equal' },
  { repo: 'pgroll', lang: 'SQL', files: 230, symbols: 2380, reduction: 86.2, coverage: 87, inc: 'equal' },
  { repo: 'realworld', lang: 'Dockerfile', files: 31, symbols: 663, reduction: 86.1, coverage: 71, inc: null },
  { repo: 'gitlabhq', lang: 'Ruby', files: 49563, symbols: 453154, reduction: 85.6, coverage: 64, inc: 'equal' },
  { repo: 'bun', lang: 'Zig', files: 13546, symbols: 246734, reduction: 84.9, coverage: 89, inc: null },
  { repo: 'super-linter', lang: 'YAML', files: 298, symbols: 1734, reduction: 84.9, coverage: 76, inc: 'differs' },
  { repo: 'azure-powershell', lang: 'PowerShell', files: 147850, symbols: 2909052, reduction: 84.3, coverage: 95, inc: 'equal' },
  { repo: 'googleapis', lang: 'Protobuf', files: 8653, symbols: 340984, reduction: 84.2, coverage: 80, inc: 'equal' },
  { repo: 'hello-world-javascript-action', lang: 'YAML', files: 15, symbols: 92, reduction: 83.4, coverage: 68, inc: null },
  { repo: 'apisix', lang: 'Lua', files: 577, symbols: 14753, reduction: 83.3, coverage: 82, inc: 'equal' },
  { repo: 'openapi-specification', lang: 'Protobuf', files: 26, symbols: 2584, reduction: 83.2, coverage: 62, inc: null },
  { repo: 'telegram-ios', lang: 'Swift', files: 16323, symbols: 635471, reduction: 82.9, coverage: 91, inc: 'equal' },
  { repo: 'plug_crypto', lang: 'Elixir', files: 12, symbols: 157, reduction: 82.6, coverage: 50, inc: 'equal' },
  { repo: 'psreadline', lang: 'PowerShell', files: 100, symbols: 2942, reduction: 82.3, coverage: 88, inc: 'equal' },
  { repo: 'grpc-proto', lang: 'Protobuf', files: 26, symbols: 1075, reduction: 82.0, coverage: 53, inc: 'equal' },
  { repo: 'actions-cache', lang: 'YAML', files: 25, symbols: 533, reduction: 81.7, coverage: 81, inc: null },
  { repo: 'sql-server-samples', lang: 'SQL', files: 2342, symbols: 82802, reduction: 81.6, coverage: 77, inc: 'differs' },
  { repo: 'flutter', lang: 'Dart', files: 11326, symbols: 191477, reduction: 81.4, coverage: 94, inc: 'equal' },
  { repo: 'tigerbeetle', lang: 'Zig', files: 430, symbols: 19694, reduction: 81.2, coverage: 88, inc: 'equal' },
  { repo: 'kubernetes-api', lang: 'Protobuf', files: 1513, symbols: 72375, reduction: 81.2, coverage: 93, inc: 'equal' },
  { repo: 'setup-python', lang: 'YAML', files: 33, symbols: 552, reduction: 81.0, coverage: 87, inc: null },
  { repo: 'jekyll', lang: 'Ruby', files: 255, symbols: 4807, reduction: 80.1, coverage: 61, inc: 'equal' },
  { repo: 'saleor', lang: 'GraphQL', files: 4424, symbols: 41517, reduction: 79.9, coverage: 92, inc: null },
  { repo: 'moby', lang: 'Dockerfile', files: 2320, symbols: 50666, reduction: 79.9, coverage: 91, inc: 'equal' },
  { repo: 'spring-petclinic', lang: 'Groovy', files: 67, symbols: 702, reduction: 79.5, coverage: 74, inc: 'equal' },
  { repo: 'drift', lang: 'Dart', files: 1454, symbols: 22438, reduction: 79.4, coverage: 93, inc: 'equal' },
  { repo: 'zig', lang: 'Zig', files: 17088, symbols: 775253, reduction: 79.3, coverage: 72, inc: 'equal' },
  { repo: 'ecto', lang: 'Elixir', files: 126, symbols: 5679, reduction: 78.7, coverage: 88, inc: 'equal' },
  { repo: 'rust', lang: 'YAML', files: 37540, symbols: 432742, reduction: 78.5, coverage: 93, inc: 'differs' },
  { repo: 'swagger-petstore', lang: 'Protobuf', files: 36, symbols: 405, reduction: 78.5, coverage: 72, inc: null },
  { repo: 'protovalidate', lang: 'Protobuf', files: 266, symbols: 13313, reduction: 78.4, coverage: 76, inc: 'equal' },
  { repo: 'maven-wrapper', lang: 'XML', files: 90, symbols: 1132, reduction: 78.2, coverage: 70, inc: null },
  { repo: 'swift', lang: 'Swift', files: 27300, symbols: 474747, reduction: 78.0, coverage: 81, inc: 'equal' },
  { repo: 'vaden', lang: 'Dart', files: 341, symbols: 2260, reduction: 77.8, coverage: 86, inc: 'equal' },
  { repo: 'libxev', lang: 'Zig', files: 53, symbols: 2146, reduction: 77.7, coverage: 79, inc: 'equal' },
  { repo: 'phoenix', lang: 'Elixir', files: 209, symbols: 5437, reduction: 77.1, coverage: 88, inc: 'equal' },
  { repo: 'gradle', lang: 'Groovy', files: 25191, symbols: 195528, reduction: 77.0, coverage: 76, inc: 'equal' },
  { repo: 'dart-sdk', lang: 'Dart', files: 27645, symbols: 585165, reduction: 76.9, coverage: 96, inc: 'differs' },
  { repo: 'elixir', lang: 'Elixir', files: 575, symbols: 23241, reduction: 76.5, coverage: 93, inc: 'equal' },
  { repo: 'spark', lang: 'Scala', files: 10058, symbols: 339330, reduction: 76.1, coverage: 94, inc: 'equal' },
  { repo: 'rails', lang: 'Ruby', files: 4301, symbols: 59024, reduction: 75.9, coverage: 61, inc: 'equal' },
  { repo: 'podinfo', lang: 'YAML', files: 192, symbols: 1949, reduction: 75.5, coverage: 80, inc: 'equal' },
  { repo: 'swift-collections', lang: 'Swift', files: 745, symbols: 19328, reduction: 75.4, coverage: 97, inc: 'equal' },
  { repo: 'swift-argument-parser', lang: 'Swift', files: 175, symbols: 5495, reduction: 74.7, coverage: 93, inc: 'equal' },
  { repo: 'immich', lang: 'Dockerfile', files: 2065, symbols: 43943, reduction: 74.7, coverage: 93, inc: 'equal' },
  { repo: 'maven-mvnd', lang: 'XML', files: 290, symbols: 3656, reduction: 74.3, coverage: 95, inc: null },
  { repo: 'flink', lang: 'XML', files: 17202, symbols: 307975, reduction: 73.9, coverage: 99, inc: null },
  { repo: 'lila', lang: 'Scala', files: 8854, symbols: 307105, reduction: 73.6, coverage: 99, inc: 'equal' },
  { repo: 'os-lib', lang: 'Scala', files: 66, symbols: 1778, reduction: 73.0, coverage: 84, inc: 'equal' },
  { repo: 'hasura-graphql-engine', lang: 'GraphQL', files: 7488, symbols: 205055, reduction: 72.7, coverage: 89, inc: 'equal' },
  { repo: 'maven', lang: 'XML', files: 6956, symbols: 83845, reduction: 72.5, coverage: 99, inc: null },
  { repo: 'docker-compose', lang: 'Dockerfile', files: 596, symbols: 9112, reduction: 71.7, coverage: 95, inc: 'equal' },
  { repo: 'cats-effect', lang: 'Scala', files: 473, symbols: 9800, reduction: 71.0, coverage: 96, inc: 'equal' },
  { repo: 'plausible-analytics', lang: 'Elixir', files: 1548, symbols: 65548, reduction: 70.9, coverage: 96, inc: 'equal' },
  { repo: 'spring-framework', lang: 'XML', files: 10447, symbols: 149700, reduction: 70.9, coverage: 95, inc: null },
  { repo: 'elasticsearch', lang: 'Groovy', files: 35011, symbols: 718102, reduction: 70.8, coverage: 97, inc: 'equal' },
  { repo: 'flyway', lang: 'SQL', files: 864, symbols: 14806, reduction: 69.8, coverage: 92, inc: null },
  { repo: 'cats-parse', lang: 'Scala', files: 38, symbols: 1764, reduction: 69.4, coverage: 80, inc: 'equal' },
  { repo: 'zio', lang: 'Scala', files: 963, symbols: 31544, reduction: 69.2, coverage: 98, inc: 'equal' },
  { repo: 'awesome-compose', lang: 'Dockerfile', files: 201, symbols: 1662, reduction: 69.2, coverage: 83, inc: 'equal' },
  { repo: 'junit5', lang: 'XML', files: 1955, symbols: 24289, reduction: 67.4, coverage: 91, inc: null },
  { repo: 'gradle-profiler', lang: 'Groovy', files: 408, symbols: 3426, reduction: 67.3, coverage: 85, inc: 'equal' },
  { repo: 'liquibase-test-harness', lang: 'SQL', files: 2603, symbols: 10530, reduction: 67.0, coverage: 73, inc: 'equal' },
  { repo: 'alamofire', lang: 'Swift', files: 112, symbols: 10727, reduction: 66.6, coverage: 91, inc: 'equal' },
  { repo: 'supabase', lang: 'SQL', files: 7937, symbols: 206815, reduction: 61.1, coverage: 95, inc: 'equal' },
  { repo: 'apollo-kotlin', lang: 'GraphQL', files: 1930, symbols: 48763, reduction: 59.7, coverage: 87, inc: 'equal' },
  { repo: 'localsend', lang: 'Dart', files: 565, symbols: 36713, reduction: 59.0, coverage: 88, inc: 'equal' },
  { repo: 'graphql-java', lang: 'GraphQL', files: 1170, symbols: 479852, reduction: 46.8, coverage: 90, inc: null },
  { repo: 'swift-tagged', lang: 'Swift', files: 10, symbols: 265, reduction: 42.1, coverage: 54, inc: 'equal' },
  // Both rows were measured on 0.13.0, before the stored-block guard. They stay
  // at their measured values because that is what those builds did, and the note
  // above the table says what 0.18.0 does instead — re-measured on the real
  // corpus at its pinned commit, docker-getting-started reproduces -166.9%
  // without the guard and reads +30.2% with it.
  { repo: 'openshift-origin', lang: 'YAML', files: 3377, symbols: 56663, reduction: -141.8, coverage: 91, inc: 'equal' },
  { repo: 'docker-getting-started', lang: 'Dockerfile', files: 23, symbols: 7370, reduction: -166.9, coverage: 65, inc: 'equal' },
] as const

/**
 * Per-repository task-context reduction: the benchmark section's bars.
 *
 * `policy` is the full escalating policy (L1 graph facts → L2 budgeted projections →
 * L3 exact source with a stated reason) plus a validation step, measured against a
 * full-source agent doing the same task including the same step's raw output.
 * `budgeted` is the same policy without escalation, kept because the gap between the
 * two is the argument for the L3 step. `recall` is edit_target_recall at a
 * 1,600-token budget — quoted per row so a reader can see which repositories buy
 * their reduction with a worse chance of the edit target being in the budget.
 *
 * Percentages as NUMBERS, not display strings. They were "−89%" — with a typographic
 * U+2212 MINUS, which is correct for prose and makes `parseFloat` return NaN, so every
 * bar computed a NaN width and rendered empty. The view formats; this file measures.
 *
 * neofetch is deliberately absent even though it was measured. Its whole program is
 * one 10,572-line shell script, so baseline A reads that single file at 120,768
 * tokens and Archis "wins" by 98.9% — the most flattering row in the corpus, and
 * degenerate. It stays in REPRESENTATION, where the coverage column exposes the
 * trade-off (−98.4% reduction at 21% coverage) in the same glance. Dropping the row
 * that helps while keeping the rows that hurt is the only defensible direction.
 *
 * The two HCL corpora are absent for a different reason: when this was measured
 * Terraform had no call graph, and the task set is built from symbols with an incoming
 * CALLS edge. That premise is gone — archis#557 made every `function_call` in a `.tf`
 * a CALLS edge — so the two rows are missing because nobody has re-run fase D over
 * them, not because there is nothing to price. Their representation figures stand in
 * the table below.
 */
export const TASK_CONTEXT = [
  { repo: 'Archis (this repo)', lang: 'Rust', budgeted: 83.8, policy: 92.7, recall: 1.0 },
  { repo: 'gin', lang: 'Go', budgeted: 54.4, policy: 93.1, recall: 1.0 },
  { repo: 'caddy', lang: 'Go', budgeted: 82.1, policy: 93.2, recall: 0.95 },
  { repo: 'prometheus', lang: 'Go', budgeted: 81.4, policy: 92.7, recall: 1.0 },
  { repo: 'architecture-samples', lang: 'Kotlin', budgeted: 50.6, policy: 90.0, recall: 1.0 },
  { repo: 'detekt', lang: 'Kotlin', budgeted: -20.2, policy: 89.4, recall: 1.0 },
  { repo: 'kotlinx.coroutines', lang: 'Kotlin', budgeted: 59.4, policy: 91.7, recall: 1.0 },
  { repo: 'NestJS', lang: 'TypeScript', budgeted: 50.2, policy: 90.1, recall: 1.0 },
  { repo: 'vuejs/core', lang: 'TypeScript', budgeted: 76.8, policy: 91.0, recall: 1.0 },
  { repo: 'angular', lang: 'TypeScript', budgeted: 80.3, policy: 91.3, recall: 1.0 },
  { repo: 'django', lang: 'Python', budgeted: 43.5, policy: 91.7, recall: 0.95 },
  { repo: 'FastAPI', lang: 'Python', budgeted: 81.6, policy: 93.7, recall: 0.95 },
  { repo: 'flask', lang: 'Python', budgeted: 79.7, policy: 93.3, recall: 0.95 },
  { repo: 'spring-boot', lang: 'Java', budgeted: 45.4, policy: 89.9, recall: 0.95 },
  { repo: 'guava', lang: 'Java', budgeted: 75.4, policy: 91.6, recall: 0.95 },
  { repo: 'RxJava', lang: 'Java', budgeted: 62.2, policy: 91.4, recall: 0.65 },
  { repo: 'PowerShell', lang: 'C#', budgeted: 93.0, policy: 95.0, recall: 0.95 },
  { repo: 'Avalonia', lang: 'C#', budgeted: 65.0, policy: 91.6, recall: 0.95 },
  { repo: 'Newtonsoft.Json', lang: 'C#', budgeted: 85.4, policy: 92.6, recall: 0.95 },
  { repo: 'nvm', lang: 'Bash', budgeted: 61.5, policy: 94.8, recall: 0.65 },
] as const

/**
 * Representation reduction per repository, ordered by reduction.
 *
 * Sorted best-to-worst on purpose: read down the reduction column and the coverage
 * column moves against it. The top row (neofetch, −98.4%) has the worst coverage in
 * the corpus (21%) and the bottom row (detekt, −58.6%) has one of the best (95%).
 * That inverse relationship IS the finding, and it is only visible if the weak rows
 * are on the page.
 *
 * Every row is unchanged from the 0.6.0 measurement to the decimal. That is not a
 * copied table — it is the re-run reproducing itself, which is what a deterministic
 * compiler should do across seven releases that did not touch the renderer.
 */
export const REPRESENTATION = [
  { repo: 'neofetch', lang: 'Bash', reduction: 98.4, coverage: '21%' },
  { repo: 'nvm', lang: 'Bash', reduction: 88.6, coverage: '83%' },
  { repo: 'terraform-aws-eks', lang: 'HCL', reduction: 86.3, coverage: '69%' },
  { repo: 'caddy', lang: 'Go', reduction: 82.6, coverage: '92%' },
  { repo: 'prometheus', lang: 'Go', reduction: 80.3, coverage: '94%' },
  { repo: 'terraform-aws-vpc', lang: 'HCL', reduction: 79.7, coverage: '73%' },
  { repo: 'PowerShell', lang: 'C#', reduction: 77.7, coverage: '99%' },
  { repo: 'architecture-samples', lang: 'Kotlin', reduction: 77.5, coverage: '79%' },
  { repo: 'angular', lang: 'TypeScript', reduction: 77.3, coverage: '89%' },
  { repo: 'FastAPI', lang: 'Python', reduction: 76.1, coverage: '81%' },
  { repo: 'gin', lang: 'Go', reduction: 75.4, coverage: '83%' },
  { repo: 'Newtonsoft.Json', lang: 'C#', reduction: 75.2, coverage: '99%' },
  { repo: 'vuejs/core', lang: 'TypeScript', reduction: 75.1, coverage: '86%' },
  { repo: 'guava', lang: 'Java', reduction: 71.9, coverage: '98%' },
  { repo: 'flask', lang: 'Python', reduction: 71.2, coverage: '82%' },
  { repo: 'django', lang: 'Python', reduction: 70.8, coverage: '78%' },
  { repo: 'Avalonia', lang: 'C#', reduction: 70.0, coverage: '99%' },
  { repo: 'NestJS', lang: 'TypeScript', reduction: 69.8, coverage: '90%' },
  { repo: 'RxJava', lang: 'Java', reduction: 69.3, coverage: '98%' },
  { repo: 'kotlinx.coroutines', lang: 'Kotlin', reduction: 66.1, coverage: '96%' },
  { repo: 'spring-boot', lang: 'Java', reduction: 65.8, coverage: '90%' },
  { repo: 'detekt', lang: 'Kotlin', reduction: 58.6, coverage: '95%' },
] as const

/**
 * The runtime layer: everything an agent turn produces that is not source code.
 *
 * This is new to the page at 0.13.0, and it is a different product claim from the two
 * above. Those are about what Archis *serves*; this is about what Archis does to the test
 * run, build log and linter report the agent generates for itself — output that never
 * went through the compiler and, until this release, was cut at 4,000 characters with
 * the remainder simply discarded.
 *
 * The headline is deliberately NOT the compression ratio. Any tool can drop 90% of a
 * log; the number that matters is whether the dropped part is still reachable, and
 * whether what survived is the part that says what went wrong. Hence `before`/`after`
 * pairs: each row is the fallback that shipped before against the codec that ships
 * now, on the same fixtures with the same oracles.
 */
export const RUNTIME = [
  {
    signal: 'A shortened output you can read back',
    before: '0%',
    after: '100%',
    detail:
      'Every lossy run now names where its original lives — a session handle or a local:// artifact — and both verify a content hash on every read.',
    source:
      'results/artifacts/REPORT_fase_g.md — 8 fixtures, 1,500-token budget against the 4,000-character cap it replaced; 5 lossy runs, 0 recoverable before, 5 of 5 after. Slowest recovery 103 ms wall against a 250 ms gate, Archis 0.13.0',
  },
  {
    signal: 'Failure lines that survive compression',
    before: '50%',
    after: '100%',
    detail:
      'Structured parsers for cargo, go test, pytest and ESLint render every failure verbatim. The old substring scan missed half of them — and 100% of the lines containing none of its eight keywords, which is where the failure actually is.',
    source:
      'results/artifacts/REPORT_fase_h.md — 6 fixtures with oracles; mean signal recall 50% → 100%, keyword-blind recall 50% → 100%, mean reduction 32.3% → 78.9%, all gates PASS, Archis 0.13.0',
  },
  {
    signal: 'The long tail no parser covers',
    before: '72.1%',
    after: '92.6%',
    detail:
      'A declarative filter DSL for docker, git, gradle and terraform. Nothing in it executes anything, and an on-disk filter is inert until you consent to it by content hash.',
    source:
      'results/artifacts/REPORT_fase_i.md — 4 command fixtures, mean reduction 72.1% (text.v0 fallback) → 92.6% (filter.v1) at 100% signal recall on both, Archis 0.13.0',
  },
  {
    signal: 'Documents the graph could not see',
    before: '0',
    after: '580',
    detail:
      'Markdown, JSON, logs and plain text were counted as skipped and thrown away. They are now indexed as one symbol per section — headings are a document’s API the way signatures are a module’s.',
    source:
      'results/artifacts/REPORT_fase_f.md — this repository, A/B on the same binary: 0 → 580 documents and 1,616 queryable sections, 68.2% fewer bytes stored, for +11.2% index time and +17.4% database. 100% heading recall gate, Archis 0.13.0',
  },
] as const

/**
 * Quality signals, for the readers who assume compression means loss.
 *
 * Coverage is quoted next to every reduction on purpose: without it, a reduction
 * number can be improved by simply dropping code.
 */
export const FIDELITY = [
  {
    signal: 'Symbol recall vs an independent parser',
    result:
      'Go 99.7–99.9% · Java 99.8% · Bash 74–98% · Kotlin 87–96% · TypeScript 90–94% · C# 79–95% · Python 85–92%',
    source:
      'universal-ctags as the oracle — results/official/fase_b_recall_cohort1.json and _cohort4.json, 20 repositories, re-measured on Archis 0.13.0 (previous publications inherited this row from a 0.5.0 run and argued it still applied; it no longer has to be argued). ctags is a LIBERAL oracle — it counts constructs Archis deliberately does not model — so the lows are category differences as much as losses: Avalonia 78.5%, django 84.6%. The Bash range is this corpus and this vintage, and the language has been re-measured since against a stricter oracle: archis#555 reports 97–100% on five repositories against mvdan/sh, whose reading includes the variables ctags names none of. This row stays until the 20-repository sweep is re-run.',
  },
  {
    signal: 'Declaration coverage',
    result: '69–99% across 21 repositories · 21% on a repo that is one 10k-line script',
    source:
      'results/official/REPORT_cohort1_4_multilang.md — mean_coverage per repo, Archis 0.13.0, unchanged from the 0.6.0 run on every row',
  },
  {
    signal: 'Re-indexing the same commit',
    result: 'Identical canonical graph hash and 0 parse failures on all 22 repositories',
    source:
      'results/official/REPORT_cohort1_4_multilang.md — every repo in the corpus, including three over 10,000 files',
  },
  {
    signal: 'Incremental update vs a clean rebuild',
    result: 'All 21 converge to the identical hash — a single-file update runs 1.8–14.7× faster',
    source:
      'results/official/REPORT_cohort1_4_multilang.md — add→rename→delete→recreate sequence vs indexing the final tree from scratch, 21 of 22 repos (neofetch has no probe-able source file), Archis 0.13.0. This was 17 of 21 at 0.6.0, with the three TypeScript repos and Avalonia diverging and published as an open gap; the gap is closed. The speed-up scales with the repository: guava 14.7×, gin 3.0×',
  },
  {
    signal: 'Edge precision, re-checked against source',
    result: 'Go CALLS / USES_TYPE / IMPLEMENTS 100%; most kinds ≥85% (a lower bound)',
    source: 'results/official/REPORT_fase_b_precision.md (earlier build)',
  },
  {
    signal: 'Structural F1 when a model reconstructs the original',
    result: '1.00 (codex gpt-5.5) · 0.99 (claude opus) · 0.96 (haiku) · 0.87 (sonnet)',
    source: 'results/summary.md — 56 scorable reconstruction runs',
  },
] as const

/**
 * The savings calculator's assumptions.
 *
 * A range, not a point. The old calculator multiplied a monthly bill by 30% and
 * printed the product as a saving, which quietly assumed every dollar was spent on
 * repository context Archis can compress. Real spend includes output tokens, reasoning,
 * cached reads, tool calls and tasks Archis never touches — so the reader now states
 * what share of their bill is input context, and the result is bounded by the
 * measured spread rather than by a single flattering figure.
 */
export const CALCULATOR = {
  /**
   * WHAT THESE NUMBERS ARE
   * ----------------------
   * The per-session token saving measured with Claude Code on Sonnet 5 in the
   * verification run after 0.49.0 (terragov/archis-bench runs/0.49.0-fixes/REPORT.md,
   * the private record): the `chi_shipped` arm — Archis as its installer leaves it,
   * hooks and briefing included — on prometheus, 20 clean pairs over two passes,
   * median −22.0% per pair, −50.6% on the pairs both arms solved, with quality
   * up from 0.760 to 0.974. The run calls it a strong signal, not a settled
   * constant, and the pages that use these numbers say so.
   *
   * Why not the 0.49.0 campaign itself: it found that Claude Code was not letting
   * the model use Archis (a dropped tool, deferred schemas, a briefing in a field
   * Claude Code does not read, instructions past its 2,048-character cap), and its
   * session figure measured that — no saving, Archis called in 0 of 29 sessions
   * under ten files. The fixes land in the release after 0.49.0, which is the one
   * these figures describe and the one production serves them with.
   *
   * Before that the calculator used the 0.47.0 campaign's −12.9% / −28.5% (17 pairs),
   * and before that 22%, 25% and 43%: the drop in money on three cells of the same
   * bench, measured with Codex CLI on Archis 0.18.0. Those are still real and are
   * kept below as EARLIER_AGENT_RUN; they are older and a different agent from the
   * one most visitors run, so they are not what a calculator should lead with.
   *
   * These are tokens, not dollars. A calculator that prices them assumes the
   * saved tokens cost what the average token costs, and says so in its footnote.
   * Before those, the page used 65–92% exposure figures, which overstate a bill by
   * two to three times; see TASK_CONTEXT_BAND for what exposure means.
   */
  /** Median token saving per clean pair: the typical session. */
  low: 0.22,
  /** What a calculator shows as the estimate — the typical session, not the best one. */
  expected: 0.22,
  /** Pairs both arms solved (score ≥ 0.95): 9.21 M → 4.54 M tokens. */
  high: 0.506,
  /**
   * Share of an agent bill spent on work about the repository — reading it,
   * navigating it, answering questions about it. Archis touches that and nothing
   * else, so the slider exists to stop the range being applied to spend it
   * cannot reach.
   */
  defaultContextShare: 0.5,
  /** One line for footnotes, so every page states the same basis. */
  basis:
    'per-session token saving measured with Claude Code on Sonnet 5 (Archis after 0.49.0, 20 paired sessions, Oct 2026) — an early measurement, priced at the average token',
} as const

/**
 * The public benchmark repository: harness, pinned corpora, raw data and every
 * campaign report. One constant, so a rename is one edit.
 */
export const BENCHMARK_REPO = {
  name: 'terragov/archis-benchmarks',
  url: 'https://github.com/terragov/archis-benchmarks',
} as const

/** A link to one file in the benchmark repository, on `main`. */
export function benchmarkFile(path: string): string {
  return `${BENCHMARK_REPO.url}/blob/main/${path}`
}

/**
 * The latest benchmark campaign, 2026-10-05. `integrity` and `accuracy` are computed
 * from the run's public files in terragov/archis-benchmarks (`runs/0.49.0/fase_a/*.json`,
 * `fase_b/recall.json`, `relations.json`, `precision.json`), so a reader can check
 * every one; the private report (terragov/archis-bench runs/0.49.0/REPORT.md) counts a
 * wider corpus that is not published. `symbolRecall` is the strict figure (same name
 * and same file), the one the public README leads with; `report` is the run's
 * `run.json`. The /verify page states these; when a new campaign lands, this block
 * moves with it.
 *
 * Two rows come from elsewhere, and each says so:
 *
 * - `session` is the verification run after 0.49.0 (runs/0.49.0-fixes), not the
 *   campaign: see CALCULATOR for why. `session.build` names it wherever the
 *   figures are shown.
 * - `speed` is 0.47.0's, because the 0.49.0 timings were taken on a loaded machine
 *   and are not publishable. These are 0.47.0's clean pass; the 14.8 s / 0.35 s
 *   this page showed before came from its under-load pass, which the bench
 *   README picked by mistake. The cold index is one run; the re-index of an
 *   unchanged tree is the median of three. There is no one-file-changed figure:
 *   the public repository withdraws it, since the harness wrote the same bytes on
 *   every pass and only the first one changed the file.
 */
export const LATEST_CAMPAIGN = {
  version: '0.49.0',
  date: '2026-10-05',
  report: 'runs/0.49.0/run.json',
  session: {
    build: 'the release after 0.49.0',
    agent: 'Claude Code, Sonnet 5 (effort high)',
    corpus: 'prometheus v0.54.1, 1 to 16 files to read per task',
    pairs: 20,
    totalTokens: -45.3,
    medianPerPair: -22.0,
    bothSolved: -50.6,
    readHeavy:
      'From 10 to 16 files: −52% tokens, with the score up from 0.82 to 0.98; 5 of 6 pairs were cheaper with Archis',
    smallTasks:
      'Under 10 files the tokens were about even (−3%) and the gain was in the answers: 0.74 without Archis, 0.97 with it.',
    quality: 'mean score 0.760 without Archis, 0.974 with it',
    caveat:
      'Twenty paired sessions over two passes, measured on the code that follows 0.49.0. A strong signal, not a settled number.',
  },
  integrity: { corpora: 113, deterministic: 113, incrementalProbed: 90, incrementalEqual: 89 },
  // Each figure has its own corpus in the public files: recall.json 111 repos / 22
  // languages, precision.json 99 / 22 (sampled, published as evidence, not a
  // headline), relations.json 114 / 25.
  accuracy: {
    repos: FASE_B_RUN.repos,
    languages: FASE_B_RUN.languages,
    symbolRecall: FASE_B_RUN.strict,
    symbolPrecision: 96.6,
    precisionRepos: 99,
    callRecall: 97.2,
    callRepos: 114,
    callLanguages: 25,
  },
  speed: { version: '0.47.0', repo: 'vscode', coldIndexSeconds: 14.6, noChangeSeconds: 0.28 },
} as const

/**
 * "Right the first time": the two figures the Team story leads with (#1162), each
 * with its own run. They come from different runs with different agents, and a page
 * shows them as two facts with two scopes, never pooled into one claim (VOICE.md §5).
 *
 * - `quality` is LATEST_CAMPAIGN.session's answer score: the same paired sessions as
 *   the calculator's token figures, graded by the same oracle.
 * - `consistency` is HEADLINE's output spread: how much the model writes, run to run,
 *   on the 0.18.0 Codex CLI cell. Archis 5%, the agent on its own tools 82%. It is a
 *   spread in visible output tokens, not in scores, and the copy says "the same work
 *   every run", not "the same quality".
 *
 * Both are one repository. The evidence issue (#1163) widens them; until it lands,
 * every place these appear carries `scope`.
 */
export const FIRST_TIME_RIGHT = {
  quality: {
    without: 0.76,
    with: 0.97,
    scope: `mean answer score, ${LATEST_CAMPAIGN.session.agent}, ${LATEST_CAMPAIGN.session.corpus}, ${LATEST_CAMPAIGN.session.pairs} paired sessions, ${LATEST_CAMPAIGN.session.build}`,
    /** For a space with a word budget: the agent, the repository and the sample. */
    short: `Claude Code on prometheus, ${LATEST_CAMPAIGN.session.pairs} paired sessions`,
  },
  consistency: {
    archis: 5,
    native: 82,
    scope: `run-to-run spread in what the model wrote, Codex CLI with gpt-5.5 on Archis 0.18.0, prometheus, ${AGENT_LOOP.pairs} pairs across three campaigns`,
    short: `Codex CLI on prometheus, Archis 0.18.0, ${AGENT_LOOP.pairs} pairs`,
  },
} as const

/**
 * The run the site used to lead with, kept because it is real and says something the
 * newer run does not: the same bench, priced in money, with a different agent.
 */
export const EARLIER_AGENT_RUN = {
  version: '0.18.0',
  agent: 'Codex CLI, gpt-5.5',
  corpus: 'prometheus, three cells of the same bench',
  money: '22–43% less per task',
  input: '35–62% less input read',
} as const

/** Where the whole corpus lives, for the footnote under the benchmark. */
export const BENCHMARK_NOTE =
  `These are separate measurements, and none is added to another. Indexing scale (${RUN.repos} repositories, ${fmt(RUN.files)} files, ${RUN.hashStable}/${RUN.repos} deterministic) and the median representation, about ${Math.round(RUN.medianReduction)}% smaller than the source, come from the public run ${PUBLIC_RUN}, computed from its files at build time. The 89.4–95% task-context range comes from a different set of 20 repositories and measures context shown to the agent, not the bill. The 22-row representation table is a third campaign, and the runtime figures are fixed test cases. Each published run's per-repository results are public in ${BENCHMARK_RUN.repository}; the campaign reports themselves stay in the internal record.`
